// src/authpages/timetables/CelcatCalendarConnect.js
import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Form } from 'react-bootstrap';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const CelcatCalendarConnect = ({ onCalendarEvents }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [celcatUrl, setCelcatUrl] = useState('');

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists() && userDoc.data().celcatCalendarConnected) {
        setIsConnected(true);
        setCelcatUrl(userDoc.data().celcatUrl || '');
        // Load events if connected
        fetchCelcatCalendarEvents();
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error checking Celcat Calendar connection:', err);
      setError('Failed to verify calendar connection status');
      setLoading(false);
    }
  };

  const connectCelcatCalendar = async () => {
    try {
      if (!celcatUrl.trim()) {
        setError('Please enter your Celcat URL');
        return;
      }

      setLoading(true);
      setError(null);
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to connect Celcat Calendar');
        setLoading(false);
        return;
      }
      
      // Update user profile to indicate Celcat Calendar is connected
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        celcatCalendarConnected: true,
        celcatUrl: celcatUrl,
        lastCelcatSyncedAt: new Date()
      }, { merge: true });
      
      setIsConnected(true);
      
      // Simulate fetching sample calendar events
      fetchCelcatCalendarEvents();
      
      setLoading(false);
    } catch (err) {
      console.error('Error connecting to Celcat Calendar:', err);
      setError('Failed to connect to Celcat Calendar');
      setLoading(false);
    }
  };

  const disconnectCelcatCalendar = async () => {
    try {
      setLoading(true);
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to disconnect Celcat Calendar');
        setLoading(false);
        return;
      }
      
      // Update user profile to indicate Celcat Calendar is disconnected
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        celcatCalendarConnected: false
      }, { merge: true });
      
      setIsConnected(false);
      setEvents([]);
      
      // Pass empty events array back to parent
      if (onCalendarEvents) {
        onCalendarEvents([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error disconnecting from Celcat Calendar:', err);
      setError('Failed to disconnect from Celcat Calendar');
      setLoading(false);
    }
  };

  const fetchCelcatCalendarEvents = async () => {
    try {
      // Generate sample events for the next 7 days (for demo purposes)
      const sampleEvents = generateSampleEvents();
      setEvents(sampleEvents);
      
      // Pass events to parent component
      if (onCalendarEvents) {
        onCalendarEvents(sampleEvents);
      }
    } catch (err) {
      console.error('Error fetching Celcat Calendar events:', err);
      setError('Failed to fetch calendar events');
    }
  };

  const generateSampleEvents = () => {
    // Implementation remains the same
    // ...
    return [];
  };

  const syncCalendar = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Refresh events
      fetchCelcatCalendarEvents();
      
      // Update last synced timestamp
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          lastCelcatSyncedAt: new Date()
        }, { merge: true });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error syncing Celcat Calendar:', err);
      setError('Failed to sync calendar');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      
      {isConnected ? (
        <div>
          <div className="mb-4">
            <Alert variant="success" className="mb-2">
              Celcat Timetable Connected
            </Alert>
            {events.length > 0 && (
              <p>
                <strong>{events.length}</strong> events imported
              </p>
            )}
          </div>
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="outline-primary"
              onClick={syncCalendar}
              disabled={loading}
              className="me-2"
            >
              {loading ? 'Syncing...' : 'Sync Now'}
            </Button>
            
            <Button 
              variant="outline-danger"
              onClick={disconnectCelcatCalendar}
              disabled={loading}
            >
              Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {/* Only show the URL input field when not connected */}
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Enter your Celcat calendar URL or ID"
              value={celcatUrl}
              onChange={(e) => setCelcatUrl(e.target.value)}
              className="mb-2"
            />
          </Form.Group>
          <Button 
            variant="primary" 
            className="w-100 py-2"
            onClick={connectCelcatCalendar}
            disabled={loading}
          >
            Connect Celcat Timetable
          </Button>
        </div>
      )}
    </>
  );
};

export default CelcatCalendarConnect;